import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useNavigate, useParams, useSearch, useRouter } from '@tanstack/react-router'
import { orgHintFromNext, storeLoginRedirectNotice, type LoginSearch } from '../lib/login-search'
import { useTranslation } from 'react-i18next'
import { mdiGoogle, mdiLoginVariant, mdiMicrosoft, mdiSlack } from '@mdi/js'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Logomark } from './Logomark'
import { AppIcon } from './ui/icon'
import { useAuth } from '../hooks/useAuth'
import { useVersion } from '../hooks/useVersion'
import { isServerPage, safeNextPath } from '../lib/safeNextPath'
import { apiClient, setAuthToken } from '../api/client'
import type { LoginResult, PendingOrgChoice } from '../contexts/AuthContext'
import { MfaChallengeForm } from './auth/MfaChallengeForm'
import { challengeFromLogin, describeChallenge, MfaApiError, mfaErrorKey, type LoginResponse, type PendingChallenge } from '../lib/mfa'
import {
  fetchProviders,
  providerLoginUrl,
  startPasskeyLogin,
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  type AuthProvider,
} from '../lib/passkeys'

// The backend's SSO provider slugs (internal/handlers/auth/sso_provider.go) —
// the brand mark next to "Continue with X", falling back to a generic login
// glyph for any provider type this map hasn't caught up with yet (#1395).
const PROVIDER_ICONS: Record<string, string> = {
  google: mdiGoogle,
  microsoft: mdiMicrosoft,
  slack: mdiSlack,
}

export function LoginForm() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [providers, setProviders] = useState<AuthProvider[]>([])
  const [passkeysEnabled, setPasskeysEnabled] = useState(false)
  const [passkeyError, setPasskeyError] = useState<string | null>(null)
  const [ssoError, setSsoError] = useState<string | null>(null)
  // The second step of the sign-in, when the account has two-step
  // verification (spec 2026-10-01-03).
  const [mfaChallenge, setMfaChallenge] = useState<PendingChallenge | null>(null)
  // The organization the challenge was issued for: the server may have
  // picked another one than the page's hint (spec 2026-10-14-22).
  const [mfaOrg, setMfaOrg] = useState<string>('')
  const [mfaRedirectedOrg, setMfaRedirectedOrg] = useState<string | undefined>(undefined)
  // An account with several organizations picks one before any session.
  const [orgChoice, setOrgChoice] = useState<PendingOrgChoice | null>(null)
  const loginButtonRef = useRef<HTMLButtonElement>(null)
  // Sign-in link (spec 2026-10-06-06): residents and providers mostly sign
  // in this way, without a password.
  const [linkSentTo, setLinkSentTo] = useState<string | null>(null)
  const [linkError, setLinkError] = useState<string | null>(null)
  const [linkPending, setLinkPending] = useState(false)

  const { t, i18n } = useTranslation('auth')
  // The card's own copy lives in `common` next to `app.name` (spec
  // 2026-09-17-03); `auth` keeps the flow's messages — a rejected credential,
  // the passkey and SSO strings, the recovery link.
  const { t: tCommon } = useTranslation()
  const { login, chooseLoginOrganization, loginWithTokens, loading, error, errorCode } = useAuth()
  const navigate = useNavigate()
  const router = useRouter()
  // Rendered by `/$org/login` and the org-less `/login` (spec 2026-10-14-22):
  // the organization is the path's, else the first segment of `next`, and
  // only ever a hint for the sign-in. '' on a bare /login.
  const params = useParams({ strict: false }) as { org?: string }
  const search = useSearch({ strict: false }) as LoginSearch
  const org = params.org || orgHintFromNext(search.next)
  const { next, email: queryEmail, password: queryPassword } = search
  const { access_token, refresh_token, expires_in, error: ssoErrorParam, error_description, mfa_challenge, mfa_enrollment_required, reason: sessionEndedReason } = search

  const { versionInfo } = useVersion()

  const nextRef = useRef(next)
  useEffect(() => {
    nextRef.current = next
  }, [next])

  const autoLoginAttempted = useRef(false)
  const ssoPickupAttempted = useRef(false)
  const autofillAttempted = useRef(false)

  // Redirect after any successful sign-in (password, SSO, or passkey), honoring
  // an explicit `next` and the backend's default-org hint. A session the
  // server opened in another organization than the hint drops `next` and
  // lands on that organization's home with a notice (spec 2026-10-14-22).
  const finishAuth = useCallback((result: LoginResult) => {
    if (result.orgChoice) {
      setOrgChoice(result.orgChoice)
      return
    }
    if (result.mfa) {
      setMfaOrg(result.orgCode || org)
      setMfaRedirectedOrg(result.redirectedOrgCode)
      setMfaChallenge(result.mfa)
      return
    }
    if (!result.success) return
    const sessionOrg = result.orgCode || org
    const redirected = result.redirectedOrgCode || (org && sessionOrg && sessionOrg !== org ? sessionOrg : '')
    if (redirected && org) {
      storeLoginRedirectNotice({ from: org, to: redirected })
    }
    const safeNext = redirected ? null : safeNextPath(nextRef.current)
    // A session restricted to two-step verification enrollment goes to the
    // full-screen setup page first, then on to where it was heading.
    if (result.mfaEnrollmentRequired) {
      navigate({
        to: '/mfa-setup',
        search: { org: redirected || result.defaultOrgCode || sessionOrg, next: safeNext || undefined },
      })
      return
    }
    if (safeNext && isServerPage(safeNext)) {
      window.location.assign(safeNext)
      return
    }
    if (safeNext) {
      router.history.push(safeNext)
      return
    }
    let redirectPath: string
    if (redirected) {
      redirectPath = `/${redirected}/`
    } else if (sessionOrg) {
      redirectPath = `/${sessionOrg}`
    } else if (result.defaultOrgCode) {
      redirectPath = `/${result.defaultOrgCode}`
    } else if (result.hasNoOrgs) {
      redirectPath = '/no-organization'
    } else {
      redirectPath = '/organizations'
    }
    navigate({ to: redirectPath })
  }, [navigate, router, org])

  const performLogin = async (emailValue: string, passwordValue: string) => {
    const result = await login({ email: emailValue, password: passwordValue, orgCode: org || undefined })
    finishAuth(result)
  }

  const chooseOrganization = async (code: string) => {
    if (!orgChoice) return
    const result = await chooseLoginOrganization(orgChoice.token, code)
    if (result.success || result.mfa) setOrgChoice(null)
    // A choice of another organization than the hint is a redirect too.
    finishAuth(org && code !== org ? { ...result, redirectedOrgCode: result.redirectedOrgCode || code } : result)
  }

  // Establish a session from tokens obtained via passkey, social login, or
  // the second step of a sign-in. A passkey sign-in an organization does not
  // count as two-step verification answers with a challenge instead.
  const establishSession = useCallback((tokens: { access_token: string; refresh_token: string; user: any; expires_in: number; mfa_required?: boolean; mfa_enrollment_required?: boolean; default_org_code?: string; organizations?: unknown[]; org_code?: string; redirected_org_code?: string; organization_choice_required?: boolean; organization_choices?: { code: string; name: string }[]; org_choice_token?: string }, sessionOrgHint?: string) => {
    if (tokens.organization_choice_required) {
      setOrgChoice({ token: tokens.org_choice_token ?? '', organizations: tokens.organization_choices ?? [] })
      return
    }
    if (tokens.mfa_required) {
      setMfaOrg(tokens.org_code || org)
      setMfaRedirectedOrg(tokens.redirected_org_code)
      setMfaChallenge(challengeFromLogin(tokens as Parameters<typeof challengeFromLogin>[0]))
      return
    }
    const sessionOrg = tokens.org_code || sessionOrgHint || org
    const result = loginWithTokens({ ...tokens, orgCode: sessionOrg })
    finishAuth({ ...result, orgCode: sessionOrg, redirectedOrgCode: tokens.redirected_org_code })
  }, [loginWithTokens, org, finishAuth])

  const onMfaVerified = useCallback((resp: LoginResponse) => {
    setMfaChallenge(null)
    establishSession({
      access_token: resp.access_token ?? '',
      refresh_token: resp.refresh_token ?? '',
      expires_in: resp.expires_in ?? 3600,
      user: resp.user,
      default_org_code: resp.default_org_code,
      organizations: resp.organizations,
      redirected_org_code: mfaRedirectedOrg,
    }, mfaOrg || org)
  }, [establishSession, mfaOrg, mfaRedirectedOrg, org])

  // The organization the second step and its verification run against.
  const challengeOrg = mfaOrg || org

  // An SSO callback or a password reset hands over only a challenge token:
  // fetch the factors it accepts, then ask for the code.
  const challengePickupAttempted = useRef(false)
  useEffect(() => {
    if (!mfa_challenge || !org || challengePickupAttempted.current) return
    challengePickupAttempted.current = true
    window.history.replaceState({}, '', window.location.pathname + (next ? `?next=${encodeURIComponent(next)}` : ''))
    describeChallenge(org, mfa_challenge)
      .then(setMfaChallenge)
      .catch((err) => setSsoError(t(mfaErrorKey(err instanceof MfaApiError ? err.code : null))))
  }, [mfa_challenge, org, next, t])

  // Social-login (OAuth) callback returns here with tokens (or an error) in the
  // query string. The redirect carries no user object, so we fetch /me with the
  // fresh access token before establishing the session, then strip the tokens
  // from the URL.
  useEffect(() => {
    if (ssoErrorParam) {
      setSsoError(t('ssoError', { error: error_description || ssoErrorParam }))
      window.history.replaceState({}, '', window.location.pathname)
      return
    }
    if (!access_token || !refresh_token || !org || ssoPickupAttempted.current) return
    ssoPickupAttempted.current = true
    ;(async () => {
      try {
        setAuthToken(access_token)
        const me = await apiClient.GET('/api/v1/organizations/{orgCode}/auth/me', {
          params: { path: { orgCode: org } },
        })
        const data = (me.data ?? {}) as any
        establishSession({
          access_token,
          refresh_token,
          expires_in: expires_in ?? 3600,
          user: data.user,
          mfa_enrollment_required: mfa_enrollment_required === '1',
        })
      } catch {
        setSsoError(t('passkeyLoginFailed'))
      }
    })()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access_token, refresh_token])

  // Load enabled providers + passkey availability for dynamic rendering.
  useEffect(() => {
    fetchProviders()
      .then((res) => {
        setProviders(res.data || [])
        setPasskeysEnabled(!!res.passkeys_enabled)
      })
      .catch(() => {
        /* discovery failure just hides the extra options */
      })
  }, [])

  // Conditional UI (autofill): once passkeys are enabled and the browser
  // supports autofill, arm a discoverable-credential ceremony that resolves
  // when the user picks a passkey from the email field's autofill suggestions.
  useEffect(() => {
    if (!passkeysEnabled || autofillAttempted.current) return
    autofillAttempted.current = true
    ;(async () => {
      try {
        if (!(await browserSupportsWebAuthnAutofill())) return
        const tokens = await startPasskeyLogin({ org: org || undefined, useAutofill: true })
        establishSession(tokens)
      } catch {
        /* user dismissed autofill or none available — ignore */
      }
    })()
  }, [passkeysEnabled, org, establishSession])

  useEffect(() => {
    const finalEmail = queryEmail || localStorage.getItem('email') || ''
    const finalPassword = queryPassword || localStorage.getItem('password') || ''
    setEmail(finalEmail)
    setPassword(finalPassword)
    if (finalEmail && finalPassword && !autoLoginAttempted.current) {
      autoLoginAttempted.current = true
      setTimeout(() => {
        performLogin(finalEmail, finalPassword)
      }, 100)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryEmail, queryPassword])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await performLogin(email, password)
  }

  const handlePasskeyLogin = async () => {
    setPasskeyError(null)
    try {
      if (!browserSupportsWebAuthn()) {
        setPasskeyError(t('passkeys.notSupported'))
        return
      }
      const tokens = await startPasskeyLogin({ email: email || undefined, org: org || undefined })
      establishSession(tokens)
    } catch {
      setPasskeyError(t('passkeyLoginFailed'))
    }
  }

  const requestSignInLink = async () => {
    setLinkError(null)
    setLinkSentTo(null)
    const address = email.trim()
    if (!address) {
      setLinkError(t('signInLink.emailRequired'))
      return
    }
    setLinkPending(true)
    try {
      const { response } = await apiClient.POST('/api/v1/organizations/{orgCode}/auth/sign-in-link/request', {
        params: { path: { orgCode: org } },
        body: { email: address, lang: i18n.language?.startsWith('fr') ? 'fr' : 'en' },
      })
      if (response.status === 429) {
        setLinkError(t('signInLink.rateLimited'))
        return
      }
      setLinkSentTo(address)
    } catch {
      setLinkError(t('signInLink.rateLimited'))
    } finally {
      setLinkPending(false)
    }
  }

  // SSO starts from an organization: without a hint, only passkeys remain.
  const ssoProviders = org ? providers : []
  const showAlternatives = ssoProviders.length > 0 || passkeysEnabled

  return (
    <div className="w-full max-w-sm rounded-2xl border bg-card p-8 shadow-dialog" data-testid="login-card">
      {/* The brand row carries the weight, which is why the form's own
          heading stays an <h2>: the page's <h1> would have to be allow-listed
          in lib/raw-h1-allowlist.ts, and that list only shrinks. */}
      <div className="flex items-center gap-3">
        <Logomark size={40} data-testid="login-brand-mark" />
        <div className="min-w-0">
          <div
            className="text-xl font-bold tracking-tight text-foreground"
            data-testid="login-brand-name"
          >
            {tCommon('app.name')}
          </div>
          <p className="truncate text-sm text-muted-foreground" data-testid="login-tagline">
            {tCommon('login.tagline')}
          </p>
        </div>
      </div>

      {orgChoice && !mfaChallenge ? (
        <div className="mt-6" data-testid="login-org-choice">
          <h2 className="text-lg font-semibold text-foreground">{t('orgChoice.title')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('orgChoice.description')}</p>
          <ul className="mt-4 space-y-2" data-testid="login-org-choice-list">
            {orgChoice.organizations.map((o) => (
              <li key={o.code}>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-start"
                  disabled={loading}
                  onClick={() => chooseOrganization(o.code)}
                  data-testid="login-org-choice-item"
                  data-org-code={o.code}
                >
                  <span className="truncate">{o.name}</span>
                  <span className="ml-auto pl-2 text-xs text-muted-foreground">{o.code}</span>
                </Button>
              </li>
            ))}
          </ul>
          {error && (
            <div className="mt-2 text-destructive text-sm" data-testid="login-org-choice-error">
              {errorCode === 'INVALID_TOKEN' ? t('orgChoice.expired') : error}
            </div>
          )}
          <Button
            type="button"
            variant="ghost"
            className="mt-4 w-full"
            onClick={() => setOrgChoice(null)}
            data-testid="login-org-choice-cancel"
          >
            {t('orgChoice.back')}
          </Button>
        </div>
      ) : mfaChallenge ? (
        <div className="mt-6" data-testid="mfa-step">
          <h2 className="text-lg font-semibold text-foreground">{t('mfa.challenge.title')}</h2>
          <div className="mt-4">
            <MfaChallengeForm
              org={challengeOrg}
              challenge={mfaChallenge}
              onVerified={onMfaVerified}
              onCancel={() => setMfaChallenge(null)}
              passkeysEnabled={passkeysEnabled}
            />
          </div>
        </div>
      ) : (
      <>
      <h2 className="mt-6 text-lg font-semibold text-foreground">{tCommon('login.title')}</h2>

      <form onSubmit={handleSubmit} className="mt-4 space-y-4" data-testid="login-form">
        {sessionEndedReason && (
          <div className="text-sm bg-muted p-2 rounded" role="status" data-testid="login-session-ended" data-reason={sessionEndedReason}>
            {t(`sessionEnded.${sessionEndedReason}`)}
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="email">{tCommon('login.email')}</Label>
          <Input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="username webauthn"
            data-testid="email-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">{tCommon('login.password')}</Label>
          <Input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            data-testid="password-input"
          />
        </div>

        {error && (
          <div className="text-destructive text-sm bg-destructive/10 border border-destructive/20 p-2 rounded space-y-1" data-testid="login-error">
            {/* A rejected credential is a normal, expected outcome — show the
                purpose-written message, not the backend's raw problem title,
                and always offer the way out. */}
            <p>{errorCode === 'INVALID_CREDENTIALS'
              ? t('invalidCredentials')
              : errorCode === 'mfa_no_usable_factor'
                ? t('mfa.errors.no_usable_factor')
                : error}</p>
            {/* Password reset is per organization: a bare /login has none. */}
            {org && <Link
              to="/$org/password-reset"
              params={{ org }}
              search={{ email: email || undefined }}
              className="underline underline-offset-2 font-medium"
              data-testid="forgot-password-link"
            >
              {t('forgotPassword')}
            </Link>}
          </div>
        )}
        {ssoError && (
          <div className="text-destructive text-sm bg-destructive/10 border border-destructive/20 p-2 rounded" data-testid="sso-error">
            {ssoError}
          </div>
        )}

        <Button
          ref={loginButtonRef}
          type="submit"
          disabled={loading}
          className="w-full"
          data-testid="login-button"
        >
          {loading ? tCommon('login.submitting') : tCommon('login.submit')}
        </Button>

        {org && <Button
          type="button"
          variant="outline"
          className="w-full"
          disabled={linkPending}
          onClick={requestSignInLink}
          data-testid="sign-in-link-button"
        >
          {linkPending ? t('signInLink.requesting') : t('signInLink.request')}
        </Button>}
        {linkSentTo && (
          <div className="text-sm bg-muted p-2 rounded" data-testid="sign-in-link-sent">
            {t('signInLink.sent', { email: linkSentTo })}
          </div>
        )}
        {linkError && (
          <div className="text-destructive text-sm bg-destructive/10 border border-destructive/20 p-2 rounded" data-testid="sign-in-link-request-error">
            {linkError}
          </div>
        )}

        {/* The error box already carries the recovery link, so the standing
            affordance only shows when there is no error to attach it to. */}
        {!error && org && (
        <div className="text-center text-sm">
          <Link
            to="/$org/password-reset"
            params={{ org }}
            search={{ email: email || undefined }}
            className="text-muted-foreground hover:text-foreground underline underline-offset-2"
            data-testid="forgot-password-link-footer"
          >
            {t('forgotPassword')}
          </Link>
        </div>
        )}
      </form>

      {showAlternatives && (
        <div className="mt-6" data-testid="auth-alternatives">
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">{t('orContinueWith')}</span>
            </div>
          </div>

          <div className="space-y-2">
            {passkeysEnabled && (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handlePasskeyLogin}
                data-testid="passkey-login-button"
              >
                {t('signInWithPasskey')}
              </Button>
            )}
            {ssoProviders.map((p) => (
              <Button
                key={p.type}
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => {
                  // Back on the org's login page, which hands the tokens over
                  // to /login with `next` kept (spec 2026-10-14-22).
                  window.location.href = providerLoginUrl(p.type, org, `/${org}/login${next ? `?next=${encodeURIComponent(next)}` : ''}`)
                }}
                data-testid={`provider-login-${p.type}`}
              >
                <AppIcon path={PROVIDER_ICONS[p.type] ?? mdiLoginVariant} size="sm" className="mr-2" />
                {t('continueWithProvider', { provider: p.name })}
              </Button>
            ))}
          </div>

          {passkeyError && (
            <div className="mt-2 text-destructive text-sm" data-testid="passkey-error">
              {passkeyError}
            </div>
          )}
        </div>
      )}
      </>
      )}

      <div className="mt-6 text-center text-xs text-muted-foreground" data-testid="login-footer">
        <p>{tCommon('app.name')} {versionInfo && versionInfo.buildVersion}</p>
      </div>
    </div>
  )
}
